/**
 * Launch Match Component
 * Allows admins to select a world, session script, cohort, player, and transcription
 * language, then have the backend create or find a match and open the
 * game-client in a new tab.
 */
import { loadFailure, requestFailure, type FormFailure } from './save-failure-words';
import { Pill } from '../../components/pill';
import { FormField, Select, FormMessage } from '../../components/form-field';
import { useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import type { SessionScriptItem, WorldItem } from '../../api/admin/sessions/types';
import type { TestPersona } from '../../api/admin/launch/types';
import { useAdminScripts, useAdminWorlds } from '../../api/admin/sessions/hooks';
import { useLaunchMatch, useTestPersonas } from '../../api/admin/launch/hooks';
import { withUiLanguage } from '../../utils/launch-url';
import admin from './admin-dashboard.module.css';

interface LaunchMatchProps {
  onBack: () => void;
}

export function LaunchMatch({ onBack }: LaunchMatchProps) {
  const { t } = useTranslation();

  const worldsQuery = useAdminWorlds(1, 100);
  const scriptsQuery = useAdminScripts();
  const personasQuery = useTestPersonas();
  const launch = useLaunchMatch();

  const worlds: WorldItem[] = worldsQuery.data ?? [];
  const sessionScripts: SessionScriptItem[] = scriptsQuery.data ?? [];
  const personas: TestPersona[] = personasQuery.data ?? [];

  const [selectedWorldId, setSelectedWorldId] = useState('');
  const [selectedSessionScriptId, setSelectedSessionScriptId] = useState('');
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [selectedPlayerName, setSelectedPlayerName] = useState('');
  const [languageCode, setLanguageCode] = useState('');

  const isLoading = worldsQuery.isLoading || scriptsQuery.isLoading || personasQuery.isLoading;
  const isLaunching = launch.isPending;
  const firstError = [worldsQuery, scriptsQuery, personasQuery].find((q) => q.isError);
  const [pageError, setPageError] = useState<FormFailure | null>(null);
  const error = pageError ?? (firstError ? loadFailure(t, firstError.error, 'launch') : null);
  const [lastLaunchUrl, setLastLaunchUrl] = useState<string | null>(null);

  const selectedWorld = worlds.find((w) => w.id === selectedWorldId);
  const uniqueCohorts = Array.from(new Set(personas.map((p) => p.cohortId))).map((id) => {
    const persona = personas.find((p) => p.cohortId === id)!;
    return { id, name: persona.cohortName };
  });
  const playerNamesForCohort = Array.from(
    new Set(personas.filter((p) => p.cohortId === selectedCohortId).map((p) => p.playerName)),
  );

  const canLaunch = selectedWorldId && selectedCohortId && selectedPlayerName && !isLaunching;

  async function handleLaunch() {
    if (!selectedWorld || !selectedCohortId || !selectedPlayerName) return;
    setPageError(null);
    setLastLaunchUrl(null);
    try {
      const result = await launch.mutateAsync({
        gameName: selectedWorld.gameName,
        worldName: selectedWorld.name,
        sessionScriptId: selectedSessionScriptId || undefined,
        cohortId: selectedCohortId,
        playerName: selectedPlayerName,
        languageCode: languageCode || undefined,
      });
      const launchUrl = withUiLanguage(result.gameClientUrl);
      setLastLaunchUrl(launchUrl);
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      setPageError(requestFailure(t, err, 'launch'));
    }
  }

  return (
    <div className="portal-shell portal-container">
      <CommonHeader
        title={t('admin.launchMatch.title')}
        showBackButton
        onBackClick={onBack}
      />

      <main className="portal-main">
        <div className="portal-content">
          {/* The narrow column is an inner wrapper on purpose: `.portal-content`
              is the centred box (`margin: 0 auto`), so narrowing it directly
              just re-centres a smaller block instead of left-aligning it. */}
          <div className={admin.adminNarrowForm}>

            {/* Game badge */}
            <div className="flex items-center gap-2.5 mb-5">
              <Pill solid tone="brand">✈️ ponder-flight-game</Pill>
            </div>

            {isLoading && <p className={admin.adminLoading}>{t('common.loading')}</p>}

            {!isLoading && (
              <div className={admin.adminTableWrapper}>
                <div className={admin.launchMatchFields}>

                  {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

                  {/* World */}
                  <FormField label={t('admin.launchMatch.world')}>
                    {(field) => (
                      <Select {...field}
                  value={selectedWorldId}
                  onChange={(e) => setSelectedWorldId(e.target.value)}
                      >
                        <option value="">{t('admin.launchMatch.selectWorld')}</option>
                        {worlds.map((w) => (
                        <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                        ))}
                      </Select>
                    )}
                  </FormField>

                  {/* Session Script */}
                  <FormField label={t('admin.launchMatch.sessionScript')}>
                    {(field) => (
                      <Select {...field}
                  value={selectedSessionScriptId}
                  onChange={(e) => setSelectedSessionScriptId(e.target.value)}
                      >
                        <option value="">{t('admin.launchMatch.noSessionScript')}</option>
                        {sessionScripts.map((ss) => (
                        <option key={ss.id} value={ss.id}>
                        {ss.id}
                      </option>
                        ))}
                      </Select>
                    )}
                  </FormField>

                  <div className={admin.adminFormSectionDivider}>{t('admin.launchMatch.testPlayer')}</div>

                  {/* Cohort */}
                  <FormField label={t('admin.launchMatch.cohort')}>
                    {(field) => (
                      <Select {...field}
                  value={selectedCohortId}
                  onChange={(e) => {
                        setSelectedCohortId(e.target.value);
                        setSelectedPlayerName('');
                        }}
                      >
                        <option value="">{t('admin.launchMatch.selectCohort')}</option>
                        {uniqueCohorts.map((c) => (
                        <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                        ))}
                      </Select>
                    )}
                  </FormField>

                  {/* Player */}
                  <FormField label={t('admin.launchMatch.player')}>
                    {(field) => (
                      <Select {...field}
                  value={selectedPlayerName}
                  onChange={(e) => setSelectedPlayerName(e.target.value)}
                  disabled={!selectedCohortId}
                      >
                        <option value="">{t('admin.launchMatch.selectPlayer')}</option>
                        {playerNamesForCohort.map((name) => (
                        <option key={name} value={name}>
                        {name}
                      </option>
                        ))}
                      </Select>
                    )}
                  </FormField>

                  <div className={admin.adminFormSectionDivider}>{t('admin.launchMatch.settings')}</div>

                  {/* Transcription language */}
                  <FormField label={t('admin.launchMatch.language')}>
                    {(field) => (
                      <Select {...field}
                  value={languageCode}
                  onChange={(e) => setLanguageCode(e.target.value)}
                      >
                        <option value="">{t('admin.launchMatch.autoDetect')}</option>
                        <option value="pt">Português (pt)</option>
                        <option value="en">English (en)</option>
                        <option value="es">Español (es)</option>
                      </Select>
                    )}
                  </FormField>

                  {/* Launch button */}
                  <Button type="button" block size="lg" className={admin.launchMatchCta} onClick={handleLaunch} disabled={!canLaunch}>
                    {isLaunching ? t('admin.launchMatch.launching') : `🚀 ${t('admin.launchMatch.launch')}`}
                  </Button>

                  {/* Last launched URL */}
                  {lastLaunchUrl && (
                    <FormMessage tone="success">
                      <p className={admin.launchMatchLinkLabel}>
                        {t('admin.launchMatch.openedLink')}
                      </p>
                      <a
                        className={`${admin.launchMatchLink} tone-brand`}
                        href={lastLaunchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {lastLaunchUrl}
                      </a>
                    </FormMessage>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
